import React from "react";
import { Link } from "react-router-dom";
import {
  FaShip,
  FaAward,
  FaUsers,
  FaShieldAlt,
  FaMapMarkedAlt,
  FaHeart,
  FaWater,
} from "react-icons/fa";
import { GiSpeedBoat } from "react-icons/gi";
import { BsTelephoneFill } from "react-icons/bs";
import SEO from "../components/common/SEO";
import NavFilters from "../components/common/NavFilters";
import { getBreadcrumbSchema } from "../utils/structuredData";

const About = () => {
  const stats = [
    { number: "10+", label: "Years Experience" },
    { number: "5000+", label: "Happy Customers" },
    { number: "50+", label: "Daily Tours" },
    { number: "5.0", label: "Average Rating" },
  ];

  const values = [
    {
      icon: <FaShieldAlt className="text-4xl" />,
      title: "Safety First",
      description:
        "Your safety is our top priority. All our vessels are fully licensed, insured, and regularly maintained to the highest standards.",
      color: "from-blue-500 to-cyan-500",
    },
    {
      icon: <FaAward className="text-4xl" />,
      title: "Expert Crew",
      description:
        "Our experienced captains and guides are certified professionals with extensive knowledge of Albanian waters and coastline.",
      color: "from-cyan-500 to-blue-400",
    },
    {
      icon: <FaUsers className="text-4xl" />,
      title: "Customer Focus",
      description:
        "We're dedicated to providing personalized service and creating unforgettable experiences for every guest.",
      color: "from-blue-400 to-cyan-600",
    },
    {
      icon: <FaMapMarkedAlt className="text-4xl" />,
      title: "Local Expertise",
      description:
        "Born and raised in Albania, we know the hidden gems and best spots that only locals know about.",
      color: "from-cyan-600 to-blue-500",
    },
    {
      icon: <FaShip className="text-4xl" />,
      title: "Premium Fleet",
      description:
        "Modern, well-maintained boats equipped with the latest safety features and comfort amenities.",
      color: "from-blue-500 to-cyan-400",
    },
    {
      icon: <FaHeart className="text-4xl" />,
      title: "Passion for Adventure",
      description:
        "We love what we do and it shows in every tour. Our enthusiasm for Albania's beauty is contagious!",
      color: "from-cyan-400 to-blue-600",
    },
  ];

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "About", path: "/about" },
  ];

  const structuredData = getBreadcrumbSchema(breadcrumbs);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="About SeaPulse Albania | #1 Boat Tour Company in Durrës"
        description="SeaPulse Albania is Durrës' premier boat tour company. Expert local guides, modern fleet including QuickSilver 805, Bayliner & luxury yachts. 110+ five-star reviews. Trusted sea adventures on the Albanian Adriatic since day one."
        keywords="SeaPulse Albania, about SeaPulse, boat tour company Durrës, best boat tour Albania, trusted boat tours Durrës, Albanian boat tour company, professional sea guides Albania, Durrës marina boat operator, Albanian Riviera tours company"
        canonical="/about"
        structuredData={structuredData}
      />
      {/* Hero Banner */}
      <div className="relative h-[450px] md:h-[500px] lg:h-[550px] flex items-center justify-center overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1559827260-dc66d52bef19?auto=format&fit=crop&w=2000&q=80"
          alt="About SeaPulse Albania"
          className="absolute inset-0 w-full h-full object-cover brightness-90 dark:brightness-75"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-blue-900/80 via-cyan-800/70 to-blue-600/60" />
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-4 max-w-5xl mx-auto">
          <span className="inline-block bg-white/20 backdrop-blur-sm text-white text-sm font-semibold px-5 py-2 rounded-full mb-6 border border-white/30 flex items-center gap-2">
            <FaWater /> About Us
          </span>
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold text-white drop-shadow-2xl mb-6 leading-tight">
            SeaPulse Albania
          </h1>
          <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-white/95 max-w-3xl leading-relaxed drop-shadow-lg">
            Your premier partner for unforgettable adventures along Albania's
            stunning Adriatic coastline
          </p>
        </div>
      </div>

      <NavFilters />

      {/* Stats Section */}
      <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl lg:text-6xl font-extrabold bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent mb-2">
                  {stat.number}
                </div>
                <div className="text-sm md:text-base text-gray-600 dark:text-gray-400 font-medium">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Our Story Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-6">
              Our Story
            </h2>
            <div className="space-y-4 text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
              <p>
                <strong className="text-gray-900 dark:text-white">
                  SeaPulse Albania
                </strong>{" "}
                was born from a deep love for the sea and a passion for sharing
                Albania's hidden coastal treasures with the world. Founded by
                local mariners who grew up exploring these waters, we've turned
                our lifetime of knowledge into unforgettable experiences for
                travelers.
              </p>
              <p>
                What started as a single boat has grown into Albania's premier
                boat tour agency, trusted by thousands of adventurers from
                around the globe. Our mission remains unchanged: to showcase the
                breathtaking beauty of the Albanian Riviera while providing
                safe, thrilling, and authentic experiences.
              </p>
              <p>
                Every tour is crafted with care, combining adventure with
                comfort, and adrenaline with relaxation. We believe that the
                best journeys are those that create lasting memories, forge new
                friendships, and inspire a deeper connection with nature.
              </p>
            </div>
          </div>
          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1544551763-46a013bb70d5?auto=format&fit=crop&w=800&q=80"
                alt="Our team"
                className="w-full h-[400px] lg:h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-blue-900/40 to-transparent" />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-cyan-500 to-blue-600 text-white p-6 rounded-2xl shadow-xl max-w-xs">
              <div className="text-2xl font-bold mb-1">⭐ 5.0 Rating</div>
              <div className="text-sm opacity-90">Based on 1000+ reviews</div>
            </div>
          </div>
        </div>
      </div>

      {/* Our Values Section */}
      <div className="bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-4">
              What Sets Us Apart
            </h2>
            <p className="text-base sm:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto leading-relaxed">
              Our commitment to excellence and passion for the Albanian coast
              make us the perfect choice for your adventure
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <div
                key={index}
                className="group bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 dark:border-gray-700 hover:border-cyan-400 dark:hover:border-cyan-500 hover:-translate-y-2"
              >
                <div
                  className={`inline-flex p-4 rounded-xl bg-gradient-to-r ${value.color} text-white mb-6 group-hover:scale-110 transition-transform duration-300`}
                >
                  {value.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-3 group-hover:text-cyan-600 dark:group-hover:text-cyan-400 transition-colors">
                  {value.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-gradient-to-b from-gray-50 to-white dark:from-gray-800 dark:to-gray-900 py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-r from-cyan-500 to-blue-600 rounded-3xl p-1 shadow-2xl">
            <div className="bg-white dark:bg-gray-900 rounded-3xl px-8 py-12">
              <h3 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                Ready to Start Your Adventure?
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-8 text-lg max-w-2xl mx-auto">
                Join us for an unforgettable journey along Albania's most
                beautiful coastline. Book your tour today!
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  to="/boat-tours"
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white font-bold py-4 px-8 rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2 justify-center"
                >
                  <GiSpeedBoat className="text-xl" /> Book Your Tour
                </Link>
                <Link
                  to="/contact"
                  className="bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 text-gray-900 dark:text-white font-bold py-4 px-8 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 flex items-center gap-2 justify-center"
                >
                  <BsTelephoneFill className="text-lg" /> Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
